import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const Shell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  height: 100dvh;
  max-height: 100dvh;
  width: 100%;
  overflow: hidden;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
    overflow: visible;
    min-height: 100dvh;
  }
`

export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 28px;
  min-height: 0;
  background: ${({ theme }) => theme.colors.background};
  animation: ${fadeUp} 0.4s ease-out both;

  @media (max-width: 900px) {
    padding: 40px 20px 48px;
    min-height: 100dvh;
  }
`

export const FormInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 380px;
  text-align: center;
`

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`

export const BrandName = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Title = styled.h1`
  margin: 0 0 8px;
  width: 100%;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Subtitle = styled.p`
  margin: 0 0 28px;
  width: 100%;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.name === 'dark' ? theme.colors.backgroundSecondary : theme.colors.borderSubtle};
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const GoogleIcon = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export const ErrorText = styled.p`
  margin: 16px 0 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.action.danger.bg};
`

export const ErrorMailto = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
`

export const SwitchRow = styled.p`
  margin: 24px 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const SwitchLink = styled(Link)`
  color: ${({ theme }) => theme.colors.action.primary.bgActive};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const FooterNote = styled.p`
  margin: 28px 0 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const FooterLegal = styled.p`
  margin: 10px auto 0;
  max-width: 34ch;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const FooterLegalLink = styled(Link)`
  color: ${({ theme }) => theme.colors.action.primary.bgActive};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const BrandPanel = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  min-height: 0;
  padding: 28px 36px 24px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.textPrimary};
  background:
    radial-gradient(
      ellipse 80% 60% at 70% 20%,
      ${({ theme }) =>
        theme.name === 'dark' ? 'rgba(62, 207, 142, 0.12)' : 'rgba(62, 207, 142, 0.14)'},
      transparent 55%
    ),
    ${({ theme }) =>
      theme.name === 'dark' ? theme.colors.backgroundSecondary : '#f1f5f9'};
  animation: ${fadeUp} 0.5s 0.05s ease-out both;

  @media (max-width: 900px) {
    display: none;
  }
`

export const BrandPanelContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: min(960px, 100%);
  min-height: 0;
  margin-inline: auto;
  gap: 0.75rem;
`

export const BrandPanelEyebrow = styled.p`
  margin: 0;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.action.primary.bgActive};
`

export const BrandPanelHeadline = styled.h2`
  margin: 0;
  flex-shrink: 0;
  font-size: clamp(1.1rem, 1.6vw, 1.35rem);
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const BrandPanelSupport = styled.p`
  display: none;
`

export const BrandPanelPreview = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  user-select: none;

  /* Preview mock is decorative; dots remain clickable */
  & [aria-hidden='true'] {
    pointer-events: none;
  }

  & > * {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    max-width: 100%;
  }
`

export const BrandPanelGlow = styled.div`
  position: absolute;
  right: -12%;
  bottom: -18%;
  width: min(420px, 70%);
  height: min(420px, 70%);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(62, 207, 142, 0.18) 0%,
    rgba(62, 207, 142, 0.05) 40%,
    transparent 70%
  );
  pointer-events: none;
`
